import { useEffect, useState } from 'react';
import { RESET_EMAIL_COOLDOWN_SECONDS, cooldownSecondsLeft, isRateLimitError } from '@cerebro-diem/core';
import { useAuthStore } from '../../stores/authStore';
import { errorMessage } from '../../services/errors';
import { authError, authInput, authNotice, authSubmit } from '../../components/styles';

type Status =
  | { name: 'idle' }
  | { name: 'sending' }
  | { name: 'sent'; email: string }
  | { name: 'failed'; message: string };

/** Asks Supabase to email a link that opens the set-password page. */
export default function PasswordResetRequest({ initialEmail = '' }: { initialEmail?: string }) {
  const { requestPasswordReset } = useAuthStore();
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<Status>({ name: 'idle' });
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const secondsLeft = cooldownSecondsLeft(cooldownUntil, now);

  useEffect(() => {
    if (cooldownUntil === null) return;
    const timer = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= cooldownUntil) setCooldownUntil(null);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  const startCooldown = () => {
    const t = Date.now();
    setNow(t);
    setCooldownUntil(t + RESET_EMAIL_COOLDOWN_SECONDS * 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    setStatus({ name: 'sending' });
    try {
      await requestPasswordReset(address);
      setStatus({ name: 'sent', email: address });
      startCooldown();
    } catch (err) {
      if (isRateLimitError(err)) {
        // Looks exactly like success: an email went to this account under a minute ago.
        setStatus({ name: 'sent', email: address });
        startCooldown();
      } else {
        setStatus({ name: 'failed', message: `The email was not sent: ${errorMessage(err)}` });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {status.name === 'sent' && (
        <div className={authNotice} role="status">
          If {status.email} has a Cerebro account, an email with a link to set a new password is on
          its way. It can take a few minutes; check spam too. Only the newest link works.
        </div>
      )}
      {status.name === 'failed' && (
        <div className={authError} role="alert">
          {status.message}
        </div>
      )}

      <div>
        <label htmlFor="reset-email" className="block text-sm font-medium mb-2">
          Email
        </label>
        <input
          id="reset-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={authInput}
          required
        />
      </div>

      <button type="submit" disabled={status.name === 'sending' || secondsLeft > 0} className={authSubmit}>
        {status.name === 'sending'
          ? 'Sending...'
          : secondsLeft > 0
            ? `Send another link in ${secondsLeft} s`
            : status.name === 'sent'
              ? 'Send another link'
              : 'Email me a link'}
      </button>
    </form>
  );
}
